import { sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { gem, R, ramp, type Ramp } from './craft';

/**
 * Hand-made item pictures: claim tablets (ใบรับตัว) for world-boss pets and mounts. Every tablet is
 * the same lacquered plaque with the creature painted in a round window, so the family reads at a
 * glance and the window tells which one; the mount's tablet is gold-rimmed and its bird breaks out
 * of the window.
 */

const cream = ramp('#efe2c0');
const fur = ramp('#c8ccd8');
const skin = ramp('#e8b08a');
const chick = ramp('#f08a3a');
const flame = ramp('#e83a22');
const shell = ramp('#5a8a5a');
const bolt = ramp('#8ad0ff');
const plank = ramp('#b8885a');
const ember = ramp('#ff7a2a');

/** The tablet: a lacquer plaque with a rounded top, a cord through it, a cream window and a seal. */
function tablet(p: Pix, body: Ramp, rim: Ramp): void {
  // Cord.
  p.line(14, 1, 16, 4, R.red.d1).line(18, 1, 16, 4, R.red.b);
  p.poly(
    [
      [9, 6],
      [12, 4],
      [20, 4],
      [23, 6],
      [24, 9],
      [24, 29],
      [8, 29],
      [8, 9],
    ],
    rim.d1,
  );
  p.poly(
    [
      [10, 7],
      [12, 5],
      [20, 5],
      [22, 7],
      [23, 9],
      [23, 28],
      [9, 28],
      [9, 9],
    ],
    body.b,
  );
  p.line(12, 5, 20, 5, rim.l2);
  p.line(23, 9, 23, 27, rim.l1);
  p.poly(
    [
      [9, 9],
      [12, 9],
      [12, 28],
      [9, 28],
    ],
    body.d1,
  );
  p.px(16, 6, R.ink.b);
  // The window.
  p.ell(16, 15, 6.5, 6.5, rim.b);
  p.ell(16, 15, 5.5, 5.5, cream.b);
  p.ring(16, 15, 5, 5, cream.l2, -2.2, -0.4);
  // Writing and the seal.
  for (const y of [23, 25]) p.line(12, y, 17, y, body.d2);
  p.rect(18, 23, 4, 4, C.seal);
  p.px(19, 24, R.red.l2);
}

registerIcons('item', {
  // ลูกวานรทงปี้ (ใบรับตัว): the ape cub's tablet, its silver-ruffed face in the window.
  'pet-wb-ape-cub': (p) => {
    tablet(p, plank, R.bronze);
    p.ell(16, 15, 4.5, 4.5, fur.b);
    p.ell(16.5, 14.5, 3.5, 3.5, fur.l1);
    p.ell(11.5, 15, 1.5, 1.8, skin.d1).ell(20.5, 15, 1.5, 1.8, skin.b);
    p.ell(16, 16, 2.8, 2.8, skin.b);
    sprite(p, 14, 15, ['k...k', '.....', '.kkk.'], { k: R.ink.b });
    p.px(17, 14, skin.l2);
  },
  // ลูกนกเพลิง (ใบรับตัว): the firebird chick's tablet, a round orange chick with a flame tuft.
  'pet-wb-firebird-chick': (p) => {
    tablet(p, plank, R.bronze);
    p.ell(15.5, 16, 4, 3.6, chick.d1);
    p.ell(16, 15.5, 3, 2.8, chick.b);
    p.px(17, 14, chick.l2);
    sprite(p, 14, 9, ['.f.f', 'fFfF', '.ff.'], { f: flame.b, F: R.gold.l1 });
    p.rect(19, 15, 2, 1, R.gold.b);
    p.px(18, 14, R.ink.b);
    p.line(13, 16, 15, 17, chick.d2);
  },
  // ลูกเต่าคะนอง (ใบรับตัว): the thunder-turtle hatchling's tablet, a small turtle from above.
  'pet-wb-turtle-hatchling': (p) => {
    tablet(p, plank, R.bronze);
    const limb = ramp('#c8c06a');
    sprite(
      p,
      11,
      9,
      [
        '.....hh.....',
        '.....hh.....',
        '.ll.ssss.ll.',
        '..lsSSSSsl..',
        '..sSsBsSss..',
        '..sSSBBSss..',
        '..ssSBsSss..',
        '..lsssssdl..',
        '.ll.sdds.ll.',
        '.....tt.....',
      ],
      { h: limb.b, l: limb.d1, s: shell.d1, S: shell.l1, B: bolt.l1, d: shell.d2, t: limb.d1 },
    );
    p.px(16, 9, R.ink.b);
  },
  // นกเพลิงปี้ฟาง (ใบรับตัว, legend): a wide gold-rimmed plaque, the firebird mount in flight across a
  // pale sun.
  'mount-wb-firebird': (p) => {
    const night = ramp('#2a2a3a');
    p.line(14, 2, 16, 6, R.red.d1).line(18, 2, 16, 6, R.red.b);
    p.poly(
      [
        [4, 7],
        [28, 7],
        [30, 9],
        [30, 25],
        [28, 27],
        [4, 27],
        [2, 25],
        [2, 9],
      ],
      R.gold.d1,
    );
    p.rect(4, 9, 25, 17, night.b);
    p.line(4, 7, 28, 7, R.gold.l2);
    p.line(30, 9, 30, 25, R.gold.l1);
    p.rect(4, 9, 3, 17, night.d1);
    // A thin ring of flame behind the bird.
    p.ring(19, 16, 6, 6, ember.d1, -2.8, 1.2);
    sprite(
      p,
      4,
      8,
      [
        '..W.....................',
        '..WW..............FF....',
        '...Ww............fFf....',
        '...Www..........hhhh....',
        '....Wwww.......hhhehkk..',
        '.....wwwww....nhhh......',
        '......wwwwww.nnn........',
        'TT.....wwwwbbnn.........',
        '.TTTt...bbbbbbn.........',
        '...TTTttbbbbbbb.........',
        'TTTTTTTtbbbbbb..........',
        '..ttttt..bbbd...........',
        '...........l............',
        '...........l............',
        '..........ll............',
      ],
      {
        F: R.gold.l2,
        f: flame.b,
        h: flame.b,
        e: R.ink.b,
        k: R.gold.b,
        n: flame.b,
        W: R.gold.l1,
        w: chick.b,
        b: flame.d1,
        T: R.gold.l1,
        t: ember.b,
        d: flame.d2,
        l: ember.l1,
      },
    );
    p.rect(24, 22, 4, 3, C.seal);
    p.px(25, 23, R.red.l2);
    gem(p, 4, 8, 1.4, R.jade);
    gem(p, 29, 8, 1.4, R.jade);
  },
});
